import {JsonRpcProvider, FailoverRpcProvider} from '@near-js/providers';
import {Account} from '@near-js/accounts';
import {FungibleToken} from '@near-js/tokens';
import getConfig from '../config';

const nearConfig = getConfig(process.env.NODE_ENV || 'development');

const NEAR_CONFIG = {
    networkId: nearConfig.networkId,
    rpcUrls: [
        // Add better RPC URLs based on network - prioritize ones with good CORS support
        ...(nearConfig.networkId === 'mainnet'
            ? [
                "https://near.lava.build:443",
                "https://free.rpc.fastnear.com",
                "https://rpc.mainnet.near.org"
            ]
            : ["https://rpc.testnet.near.org"]
        )
    ],
    network: nearConfig.networkId
};

export class NearService {
    constructor() {
        this.provider = null;
        this.initialized = false;
    }

    static getConfig() {
        return NEAR_CONFIG;
    }

    async initialize() {
        const config = NearService.getConfig();

        // Create FailoverRpcProvider with all RPC URLs
        this.provider = new FailoverRpcProvider(config.rpcUrls.map(url =>
            new JsonRpcProvider({url})
        ));

        this.initialized = true;
    }

    async getAccountBalance(accountId) {
        if (!this.provider || !this.initialized) {
            throw new Error('NEAR service not initialized');
        }
        try {
            const account = new Account(accountId, this.provider, undefined);
            const accountState = await account.getState();
            return BigInt(accountState.balance.total);
        } catch (error) {
            console.error('Error fetching account balance:', error);
            throw error;
        }
    }

    async getAccountState(accountId) {
        if (!this.provider || !this.initialized) {
            throw new Error('NEAR service not initialized');
        }
        try {
            const account = new Account(accountId, this.provider, undefined);
            return await account.getState();
        } catch (error) {
            console.error('Error fetching account state:', error);
            throw error;
        }
    }

    async accountExists(accountId) {
        if (!this.provider || !this.initialized) {
            return false;
        }
        try {
            await this.provider.query({
                request_type: 'view_account',
                finality: 'final',
                account_id: accountId
            });
            return true;
        } catch (error) {
            console.log(error);
            return false;
        }
    }

    async callViewFunction(contractId, methodName, args = {}) {
        if (!this.provider || !this.initialized) {
            throw new Error('NEAR service not initialized');
        }

        try {
            const argsBase64 = Buffer.from(JSON.stringify(args)).toString('base64');

            const result = await this.provider.query({
                request_type: 'call_function',
                finality: 'final',
                account_id: contractId,
                method_name: methodName,
                args_base64: argsBase64,
            });

            // Parse the result
            return JSON.parse(Buffer.from(result.result).toString());
        } catch (error) {
            console.error(`Error calling ${methodName} on ${contractId}:`, error);
            throw error;
        }
    }

    getCurrentProvider() {
        return this.provider;
    }

    isInitialized() {
        return this.initialized && this.provider !== null;
    }
}

export const nearService = new NearService();
